import { Head, Link, router, useForm } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { useState } from 'react'
import { Save, KeyRound, ShieldCheck, AlertTriangle, ArrowLeft, Trash2, Plug } from 'lucide-react'

interface PemSummary {
  line_count: number
  char_count: number
  has_real_newlines: boolean
  expected_kind: string
  detected_kind: string | null
  looks_valid: boolean
}

interface Source {
  base_url: 'database' | 'env' | 'default'
  cert: 'database' | 'env' | 'missing'
  key: 'database' | 'env' | 'missing'
  hmac: 'database' | 'env' | 'missing'
}

interface Props extends PageProps {
  ffr_api_base_url: string | null
  ffr_hmac_secret_set: boolean
  ffr_client_cert_set: boolean
  ffr_client_key_set: boolean
  cert_summary: PemSummary | null
  key_summary: PemSummary | null
  source: Source
  configured: boolean
  effective_base_url: string
}

const Badge = ({ source }: { source: 'database' | 'env' | 'default' | 'missing' }) => {
  const map: Record<string, string> = {
    database: 'bg-forest-green/15 text-forest-green',
    env:      'bg-gold/20 text-charcoal',
    default:  'bg-charcoal/10 text-charcoal/70',
    missing:  'bg-burgundy/15 text-burgundy',
  }
  const label: Record<string, string> = {
    database: 'Stored in database',
    env:      'Using environment variable',
    default:  'Using default',
    missing:  'Not set',
  }
  return <span className={`inline-block px-2 py-0.5 text-xs rounded ${map[source]}`}>{label[source]}</span>
}

export default function FfrCredentialsEdit(props: Props) {
  const { data, setData, patch, processing } = useForm({
    ffr_api_base_url: props.ffr_api_base_url || '',
    ffr_client_cert_pem: '',
    ffr_client_key_pem: '',
    ffr_hmac_secret: '',
  })

  const [testing, setTesting] = useState(false)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    patch('/admin/ffr_credentials', { preserveScroll: true })
  }

  const clearField = (field: string) => {
    if (!confirm(`Clear ${field}? It will fall back to the environment variable if one is set.`)) return
    router.delete(`/admin/ffr_credentials/${field}`, { preserveScroll: true })
  }

  const runTest = () => {
    setTesting(true)
    router.post('/admin/ffr_credentials/test', {}, { onFinish: () => setTesting(false) })
  }

  return (
    <AppLayout>
      <Head title="FFR Credentials" />
      <div className="max-w-4xl mx-auto p-6">
        <Link href="/admin/pending_commissions" className="inline-flex items-center gap-1 text-sm text-charcoal/60 hover:text-forest-green mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Pending Commissions
        </Link>

        <h1 className="text-3xl font-serif text-forest-green mb-2 flex items-center gap-2">
          <KeyRound className="w-6 h-6" /> FFR Credentials
        </h1>
        <p className="text-sm text-charcoal/70 mb-6">
          Store your FFR Member API credentials in the application database. This avoids the
          newline-formatting issues that can plague deployment secrets. Values stored here take
          precedence over the corresponding environment variables.
        </p>

        <div className={`mb-6 p-4 rounded-lg border ${props.configured ? 'bg-forest-green/5 border-forest-green/20' : 'bg-burgundy/5 border-burgundy/20'}`}>
          <div className="flex items-start gap-2">
            {props.configured ? <ShieldCheck className="w-5 h-5 text-forest-green flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-burgundy flex-shrink-0 mt-0.5" />}
            <div className="text-sm">
              <div className="font-semibold mb-1">
                {props.configured ? 'FFR is configured' : 'FFR is NOT fully configured yet'}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div><span className="text-charcoal/60">Base URL:</span> <span className="font-mono">{props.effective_base_url}</span> <Badge source={props.source.base_url} /></div>
                <div><span className="text-charcoal/60">HMAC Secret:</span> <Badge source={props.source.hmac} /></div>
                <div><span className="text-charcoal/60">Client Certificate:</span> <Badge source={props.source.cert} /></div>
                <div><span className="text-charcoal/60">Private Key:</span> <Badge source={props.source.key} /></div>
              </div>
            </div>
          </div>
        </div>

        {props.configured && (
          <div className="mb-6">
            <button onClick={runTest} disabled={testing} className="inline-flex items-center gap-2 px-4 py-2 rounded bg-forest-green text-cream hover:opacity-90 disabled:opacity-50">
              <Plug className="w-4 h-4" /> {testing ? 'Testing…' : 'Test Connection (live FFR call)'}
            </button>
            <p className="text-xs text-charcoal/60 mt-1">Pulls "last week" and reports the record count or the exact error message.</p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-6 bg-white border border-charcoal/10 rounded-lg p-6">
          <div>
            <label className="block text-sm font-semibold text-charcoal mb-1">FFR API Base URL</label>
            <input
              type="text"
              value={data.ffr_api_base_url}
              onChange={(e) => setData('ffr_api_base_url', e.target.value)}
              placeholder="https://api.ffrmembers.com"
              className="w-full px-3 py-2 border border-charcoal/20 rounded text-sm font-mono"
            />
            <p className="text-xs text-charcoal/60 mt-1">Leave blank to use the default (https://api.ffrmembers.com).</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-semibold text-charcoal">Client Certificate (PEM)</label>
              {props.ffr_client_cert_set && (
                <button type="button" onClick={() => clearField('ffr_client_cert_pem')} className="inline-flex items-center gap-1 text-xs text-burgundy hover:underline">
                  <Trash2 className="w-3 h-3" /> Clear stored cert
                </button>
              )}
            </div>
            {props.cert_summary && (
              <div className="mb-2 p-2 bg-cream border border-charcoal/10 rounded text-xs text-charcoal/70">
                <div>Currently stored — {props.cert_summary.char_count} chars, {props.cert_summary.line_count} line(s), newlines: {props.cert_summary.has_real_newlines ? 'yes ✓' : 'no (will be auto-fixed on use)'}.</div>
                <div>Detected PEM type: {props.cert_summary.detected_kind ? <code>{props.cert_summary.detected_kind}</code> : <span className="text-burgundy">no -----BEGIN----- header found</span>} {props.cert_summary.looks_valid ? '✓' : <span className="text-burgundy">(expected CERTIFICATE)</span>}</div>
              </div>
            )}
            <textarea
              value={data.ffr_client_cert_pem}
              onChange={(e) => setData('ffr_client_cert_pem', e.target.value)}
              placeholder={"-----BEGIN CERTIFICATE-----\nMIIDxTCCAq2g...\n-----END CERTIFICATE-----"}
              rows={10}
              className="w-full px-3 py-2 border border-charcoal/20 rounded text-xs font-mono"
            />
            <p className="text-xs text-charcoal/60 mt-1">
              Paste the entire .crt file contents including the BEGIN/END lines. Leave blank to keep the current value.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-semibold text-charcoal">Private Key (PEM)</label>
              {props.ffr_client_key_set && (
                <button type="button" onClick={() => clearField('ffr_client_key_pem')} className="inline-flex items-center gap-1 text-xs text-burgundy hover:underline">
                  <Trash2 className="w-3 h-3" /> Clear stored key
                </button>
              )}
            </div>
            {props.key_summary && (
              <div className="mb-2 p-2 bg-cream border border-charcoal/10 rounded text-xs text-charcoal/70">
                <div>Currently stored — {props.key_summary.char_count} chars, {props.key_summary.line_count} line(s), newlines: {props.key_summary.has_real_newlines ? 'yes ✓' : 'no (will be auto-fixed on use)'}.</div>
                <div>Detected PEM type: {props.key_summary.detected_kind ? <code>{props.key_summary.detected_kind}</code> : <span className="text-burgundy">no -----BEGIN----- header found</span>} {props.key_summary.looks_valid ? '✓' : <span className="text-burgundy">(expected PRIVATE KEY)</span>}</div>
              </div>
            )}
            <textarea
              value={data.ffr_client_key_pem}
              onChange={(e) => setData('ffr_client_key_pem', e.target.value)}
              placeholder={"-----BEGIN PRIVATE KEY-----\nMIIEvQIB...\n-----END PRIVATE KEY-----"}
              rows={10}
              className="w-full px-3 py-2 border border-charcoal/20 rounded text-xs font-mono"
            />
            <p className="text-xs text-charcoal/60 mt-1">
              Paste the entire .key file contents. Sensitive — only admins can see this page. Leave blank to keep the current value.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-semibold text-charcoal">HMAC Shared Secret</label>
              {props.ffr_hmac_secret_set && (
                <button type="button" onClick={() => clearField('ffr_hmac_secret')} className="inline-flex items-center gap-1 text-xs text-burgundy hover:underline">
                  <Trash2 className="w-3 h-3" /> Clear stored secret
                </button>
              )}
            </div>
            <input
              type="password"
              value={data.ffr_hmac_secret}
              onChange={(e) => setData('ffr_hmac_secret', e.target.value)}
              placeholder={props.ffr_hmac_secret_set ? '(currently stored — leave blank to keep)' : 'paste 64-char hex secret here'}
              className="w-full px-3 py-2 border border-charcoal/20 rounded text-sm font-mono"
            />
            <p className="text-xs text-charcoal/60 mt-1">The 64-character hex string from <code>hmac_secret.txt</code>.</p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={processing} className="inline-flex items-center gap-2 px-5 py-2 rounded bg-forest-green text-cream hover:opacity-90 disabled:opacity-50">
              <Save className="w-4 h-4" /> {processing ? 'Saving…' : 'Save Credentials'}
            </button>
            <span className="text-xs text-charcoal/60">Empty fields are ignored — they don't overwrite existing values.</span>
          </div>
        </form>

        <div className="mt-6 text-xs text-charcoal/60 space-y-1">
          <p><strong>Security notes:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Credentials are stored in the application database. Only administrators can access this page.</li>
            <li>Cert/key strings are auto-normalized on use: literal <code>\n</code> sequences and missing newlines are repaired automatically.</li>
            <li>If you also have FFR_* environment variables set, the database value wins. Clear a field to fall back to the env var.</li>
          </ul>
        </div>
      </div>
    </AppLayout>
  )
}
